/*
 * Copyright 2026 Sony Corporation
 */
import { AppBar, Box, Button, Container, Toolbar, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useMatch } from 'react-router-dom';

import { useAuth } from '../contexts/auth-context';

import { DashboardIcon, DevicesIcon, ProjectsIcon } from './icons';

/** A tab of the header; the one for the page the user is on (or a page under it) is marked and underlined. */
const NavTab = ({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) => {
  const { pathname } = useLocation();
  const current = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={icon}
      aria-current={current ? 'page' : undefined}
      sx={{
        color: current ? 'primary.light' : 'text.secondary',
        fontWeight: current ? 700 : 500,
        bgcolor: current ? 'rgba(144, 202, 249, 0.12)' : 'transparent',
        borderRadius: 1,
        px: 1.5,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 8,
          right: 8,
          bottom: -5,
          height: 3,
          borderRadius: 1.5,
          bgcolor: current ? 'primary.main' : 'transparent',
        },
        '&:hover': { color: current ? 'primary.light' : 'text.primary' },
      }}
    >
      {children}
    </Button>
  );
};

export const AppShell = () => {
  const { email, signOut } = useAuth();
  // A project fills the window with its panels; the other pages are a centred column.
  const onProject = useMatch('/projects/:projectId') !== null;

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <NavTab to="/runtimes" icon={<DevicesIcon />}>
            Runtimes
          </NavTab>
          <NavTab to="/projects" icon={<ProjectsIcon />}>
            Projects
          </NavTab>
          <NavTab to="/dashboard" icon={<DashboardIcon />}>
            Dashboard
          </NavTab>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {email}
          </Typography>
          <Button color="inherit" onClick={signOut}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      {onProject ? (
        <Box component="main" sx={{ flex: 1, minHeight: 0, p: 2, overflow: { xs: 'auto', md: 'hidden' } }}>
          <Outlet />
        </Box>
      ) : (
        <Box component="main" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Outlet />
          </Container>
        </Box>
      )}
    </Box>
  );
};
